import React, {useId} from "react";
import BaseInput from "components/BaseInput";
import {SUBLINE_MAX_LENGTH} from "../sponsorOptions";
import {OptionError, OptionField, OptionHint} from "./optionControls.styles";

/**
 * The poster's third line (kitecorp/transferify-ui#1868).
 *
 * Bounded by {@link SUBLINE_MAX_LENGTH}, which is the TAGLINE's own bound rather than a second number —
 * the api states the rule that way and this reads it from the same place, so the `maxLength` the browser
 * enforces and the length the server checks cannot drift.
 *
 * Not a `<fieldset>` like its siblings: `BaseInput` carries a real `<label>` already, and wrapping a
 * single labelled input in a group would announce its name twice.
 *
 * Dumb by mandate (epic #26). The sender's own page-theme picker mounts it unchanged
 * (kitecorp/transferify-ui#1869).
 */
export interface SublineFieldProps {
	label: string;
	placeholder: string;
	hint: string;
	value: string;
	onChange: (value: string) => void;
	onBlur: () => void;
	error: string | null;
}

export function SublineField(
	{label, placeholder, hint, value, onChange, onBlur, error}: SublineFieldProps,
): React.ReactElement {
	const id = useId();
	const hintId = `${id}-hint`;
	const errorId = `${id}-error`;
	return (
		<OptionField as="div">
			<BaseInput
				id={id}
				variant="outlined"
				label={label}
				placeholder={placeholder}
				value={value}
				error={error !== null}
				inputProps={{maxLength: SUBLINE_MAX_LENGTH}}
				aria-describedby={error === null ? hintId : `${hintId} ${errorId}`}
				onBlur={onBlur}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
			/>
			<OptionHint id={hintId}>{hint}</OptionHint>
			{error !== null && <OptionError id={errorId} role="alert">{error}</OptionError>}
		</OptionField>
	);
}
